import { ArrowDownUp, Check } from "lucide-react";
import type { FieldDefinition, FieldType, Sort } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Direction labels in the field's own terms — "A → Z" says more than "asc". */
function directionLabels(type: FieldType): { asc: string; desc: string } {
  switch (type) {
    case "number":
    case "money":
      return { asc: "Low → high", desc: "High → low" };
    case "date":
    case "datetime":
      return { asc: "Oldest first", desc: "Newest first" };
    case "boolean":
      return { asc: "No first", desc: "Yes first" };
    default:
      return { asc: "A → Z", desc: "Z → A" };
  }
}

/**
 * What the list is sorted by, in every layout.
 *
 * Its own toolbar control rather than a section of the column chooser (moved
 * 2026-09-15): the grid and tiles have no column headers to click and no
 * columns to choose, so sort hidden inside Columns was unreachable exactly
 * where it was needed. Table headers still sort — they write the same URL
 * state, so the button's label and the header arrows always agree.
 *
 * Edits the primary sort and keeps any tie-breaks set by shift-clicking
 * headers: rewriting the whole array would silently drop one, and the list
 * would look sorted while ranking wrongly.
 *
 * `compact` drops the "Sort: Field ↑" label for an icon-only trigger, for
 * the filter bar (#349), which puts this beside Columns and Save rather than
 * alone in a toolbar with room to spell it out.
 */
export function SortControl({
  fields,
  sort,
  compact = false,
  onChange,
}: {
  fields: FieldDefinition[];
  sort: Sort[];
  compact?: boolean;
  onChange: (next: Sort[]) => void;
}) {
  const sortable = fields.filter((field) => field.sortable);
  if (sortable.length === 0) return null;

  const primary = sort[0] ?? null;
  const primaryField = primary
    ? sortable.find((field) => field.name === primary.column)
    : undefined;
  const labels = directionLabels(primaryField?.type ?? "text");

  function choose(column: string, direction: Sort["direction"]) {
    onChange([
      { column, direction },
      ...sort.slice(1).filter((entry) => entry.column !== column),
    ]);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {compact ? (
          <Button
            size="icon-sm"
            variant="outline"
            aria-label={
              primaryField && primary
                ? `Sort: ${primaryField.label} ${primary.direction === "asc" ? "ascending" : "descending"}`
                : "Sort"
            }
          >
            <ArrowDownUp />
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <ArrowDownUp />
            {primaryField && primary ? (
              <span>
                Sort: {primaryField.label}{" "}
                <span className="text-muted-foreground">
                  {primary.direction === "asc" ? "↑" : "↓"}
                </span>
              </span>
            ) : (
              "Sort"
            )}
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-2">
        <p className="text-muted-foreground px-2 pt-1 pb-1.5 text-xs font-medium">
          Sort by
        </p>
        <div
          role="radiogroup"
          aria-label="Sort by"
          className="flex max-h-72 flex-col overflow-y-auto"
        >
          {sortable.map((field) => {
            const active = primary?.column === field.name;
            return (
              <button
                key={field.name}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  choose(field.name, primary?.direction ?? "asc");
                }}
                className={cn(
                  "hover:bg-accent focus-visible:ring-ring flex items-center justify-between rounded-md px-2 py-1.5 text-left text-sm focus-visible:ring-2 focus-visible:outline-none",
                  active && "font-medium",
                )}
              >
                {field.label}
                {active ? <Check className="size-4" aria-hidden /> : null}
              </button>
            );
          })}
        </div>

        {primary ? (
          <div className="mt-2 flex flex-col gap-2 border-t px-1 pt-2">
            <div
              role="radiogroup"
              aria-label="Direction"
              className="bg-muted grid grid-cols-2 gap-0.5 rounded-md p-0.5"
            >
              {(["asc", "desc"] as const).map((direction) => (
                <button
                  key={direction}
                  type="button"
                  role="radio"
                  aria-checked={primary.direction === direction}
                  onClick={() => {
                    choose(primary.column, direction);
                  }}
                  className={cn(
                    "focus-visible:ring-ring rounded px-2 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none",
                    primary.direction === direction
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {labels[direction]}
                </button>
              ))}
            </div>
            {sort.length > 1 ? (
              <p className="text-muted-foreground px-1 text-xs">
                Plus {sort.length - 1} tie-break
                {sort.length > 2 ? "s" : ""} set from the table headers.
              </p>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 justify-start px-2 text-xs"
              onClick={() => {
                onChange([]);
              }}
            >
              Clear sort
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
